(function () {
  'use strict';

  if (window.AxiomStoriesControllerLoaded) return;
  window.AxiomStoriesControllerLoaded = true;

  const SELECTOR = '[data-stories-section]';
  const OPEN_CLASS = 'is-open';
  const LOCK_CLASS = 'stories-viewer-open';

  function clampIndex(index, length) {
    if (!length) return 0;
    return (index + length) % length;
  }

  function pauseVideo(panel) {
    const video = panel && panel.querySelector('video');
    if (!video) return;
    video.pause();
  }

  function playVideo(panel, audioEnabled) {
    const video = panel && panel.querySelector('video');
    if (!video) return;
    video.muted = !audioEnabled;
    video.playsInline = true;
    video.play().catch(() => {});
  }

  function setPreviewToggleState(video, isPlaying) {
    const card = video && video.closest('.stories-card');
    const button = card && card.querySelector('[data-stories-preview-toggle]');
    if (!button) return;
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? button.dataset.pauseLabel : button.dataset.playLabel);
  }

  function setPanelFocusEnabled(panel, enabled) {
    if (!panel) return;
    panel.querySelectorAll('a, button').forEach((element) => {
      if (enabled) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  function getPanelFocusableElements(panel) {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && !element.closest('[inert]') && element.getClientRects().length > 0);
  }

  function setPageLocked(locked) {
    document.documentElement.classList.toggle(LOCK_CLASS, locked);
    document.body.classList.toggle(LOCK_CLASS, locked);
  }

  function StoriesSection(root) {
    this.root = root;
    this.viewer = root.querySelector('[data-stories-viewer]');
    this.triggers = Array.from(root.querySelectorAll('[data-stories-trigger]'));
    this.panels = this.viewer ? Array.from(this.viewer.querySelectorAll('[data-stories-panel]')) : [];
    this.progress = this.viewer ? Array.from(this.viewer.querySelectorAll('[data-stories-progress]')) : [];
    this.audioButtons = this.viewer ? Array.from(this.viewer.querySelectorAll('[data-stories-audio]')) : [];
    this.viewerMedia = this.viewer ? Array.from(this.viewer.querySelectorAll('[data-stories-media-deferred]')) : [];
    this.rail = root.querySelector('[data-stories-rail]');
    this.railControls = root.querySelector('[data-stories-rail-controls]');
    this.railPrevious = root.querySelector('[data-stories-rail-previous]');
    this.railNext = root.querySelector('[data-stories-rail-next]');
    this.previewVideos = Array.from(root.querySelectorAll('.stories-card__video'));
    this.previewObserver = null;
    this.viewerMediaObserver = null;
    this.railResizeObserver = null;
    this.railFrame = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.index = 0;
    this.audioEnabled = false;
    this.lastFocus = null;
    this.touchStartX = 0;
    this.viewerReady = false;
    this.boundClick = this.onClick.bind(this);
    this.boundViewerClick = this.onViewerClick.bind(this);
    this.boundViewerKeydown = this.onViewerKeydown.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundTouchStart = this.onTouchStart.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);
    this.boundBlockSelect = this.onBlockSelect.bind(this);
    this.boundSectionUnload = this.destroy.bind(this);
    this.boundRailScroll = this.onRailScroll.bind(this);
    this.boundRailResize = this.updateRailControls.bind(this);
    this.boundVisibilityChange = this.onVisibilityChange.bind(this);
    this.boundViewerMediaIntersect = this.onViewerMediaIntersect.bind(this);
    this.init();
  }

  StoriesSection.prototype.init = function () {
    if (!this.viewer || !this.panels.length) return;
    this.root.addEventListener('click', this.boundClick);
    this.root.addEventListener('shopify:block:select', this.boundBlockSelect);
    this.root.addEventListener('shopify:section:unload', this.boundSectionUnload);
    this.initRail();
    this.initPreviewVideos();
    this.initViewerMedia();
  };

  StoriesSection.prototype.initViewerMedia = function () {
    if (!this.viewerMedia.length || !('IntersectionObserver' in window)) return;

    this.viewerMediaObserver = new IntersectionObserver(this.boundViewerMediaIntersect, {
      rootMargin: '500px 0px',
      threshold: 0
    });
    this.viewerMediaObserver.observe(this.root);
  };

  StoriesSection.prototype.onViewerMediaIntersect = function (entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.hydrateViewerMedia();
  };

  StoriesSection.prototype.hydrateViewerMedia = function () {
    if (!this.viewerMedia.length) return;

    if (this.viewerMediaObserver) {
      this.viewerMediaObserver.disconnect();
      this.viewerMediaObserver = null;
    }

    this.viewerMedia.forEach((image) => {
      const picture = image.closest('picture');
      if (picture) {
        picture.querySelectorAll('source[data-stories-srcset]').forEach((sourceElement) => {
          sourceElement.srcset = sourceElement.dataset.storiesSrcset;
          sourceElement.removeAttribute('data-stories-srcset');
        });
      }
      const source = image.dataset.storiesSrc;
      const sourceSet = image.dataset.storiesSrcset;
      image.loading = 'eager';
      if (sourceSet) image.srcset = sourceSet;
      if (source) image.src = source;
      image.removeAttribute('data-stories-srcset');
      image.removeAttribute('data-stories-src');
      image.removeAttribute('data-stories-media-deferred');
    });
    this.viewerMedia = [];
  };

  StoriesSection.prototype.initRail = function () {
    if (!this.rail || !this.railControls || !this.railPrevious || !this.railNext) return;
    this.rail.addEventListener('scroll', this.boundRailScroll, { passive: true });
    window.addEventListener('resize', this.boundRailResize);
    if ('ResizeObserver' in window) {
      this.railResizeObserver = new ResizeObserver(this.boundRailResize);
      this.railResizeObserver.observe(this.rail);
    }
    this.updateRailControls();
  };

  StoriesSection.prototype.initPreviewVideos = function () {
    if (!this.previewVideos.length) return;
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    if (!('IntersectionObserver' in window)) {
      this.previewVideos.forEach((video) => setPreviewToggleState(video, false));
      return;
    }
    this.previewObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        video.dataset.storiesPreviewVisible = entry.isIntersecting && entry.intersectionRatio >= 0.55 ? 'true' : 'false';
        this.syncPreviewVideo(video);
      });
    }, { threshold: [0, 0.55, 1] });
    this.previewVideos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      this.previewObserver.observe(video);
      setPreviewToggleState(video, false);
    });
  };

  StoriesSection.prototype.portalViewer = function () {
    if (!this.viewer || this.viewer.parentElement === document.body) return;
    const viewerRadius = this.root.style.getPropertyValue('--stories-viewer-radius');
    if (viewerRadius) this.viewer.style.setProperty('--stories-viewer-radius', viewerRadius.trim());
    this.viewer.dataset.storiesPortal = 'body';
    document.body.appendChild(this.viewer);
  };

  StoriesSection.prototype.ensureViewerReady = function () {
    if (this.viewerReady || !this.viewer) return;
    this.portalViewer();
    this.viewer.addEventListener('click', this.boundViewerClick);
    this.viewer.addEventListener('keydown', this.boundViewerKeydown, true);
    this.viewer.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.viewer.addEventListener('touchend', this.boundTouchEnd, { passive: true });
    this.setIndex(this.index, false);
    this.viewerReady = true;
  };

  StoriesSection.prototype.onClick = function (event) {
    const previewToggle = event.target.closest('[data-stories-preview-toggle]');
    if (previewToggle && this.root.contains(previewToggle)) {
      this.togglePreviewVideo(previewToggle);
      return;
    }

    if (event.target.closest('[data-stories-rail-previous]')) {
      this.scrollRail(-1);
      return;
    }

    if (event.target.closest('[data-stories-rail-next]')) {
      this.scrollRail(1);
      return;
    }

    const trigger = event.target.closest('[data-stories-trigger]');
    if (trigger && this.root.contains(trigger)) {
      this.open(Number(trigger.dataset.storiesIndex || 0));
      return;
    }
  };

  StoriesSection.prototype.onViewerClick = function (event) {
    const interactiveTarget = event.target.closest('a, button, input, select, textarea, summary, [role="button"]');

    if (event.target.closest('[data-stories-close]')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-stories-prev]')) {
      this.go(-1);
      return;
    }

    if (event.target.closest('[data-stories-next]')) {
      this.go(1);
      return;
    }

    if (event.target.closest('[data-stories-audio]')) {
      this.toggleAudio();
      return;
    }

    const inactivePanel = event.target.closest('[data-stories-panel]:not(.is-active)');
    if (inactivePanel && this.viewer.contains(inactivePanel)) {
      event.preventDefault();
      this.setIndex(Number(inactivePanel.dataset.storiesIndex || 0), true);
      return;
    }

    if (interactiveTarget) return;

    const stage = event.target.closest('[data-stories-stage]');
    const activePanel = event.target.closest('[data-stories-panel].is-active');
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    if (isMobile && stage && activePanel && this.viewer.contains(stage)) {
      const rect = stage.getBoundingClientRect();
      this.go(event.clientX < rect.left + rect.width / 2 ? -1 : 1);
    }
  };

  StoriesSection.prototype.onKeydown = function (event) {
    if (!this.isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(1);
    }
  };

  StoriesSection.prototype.onViewerKeydown = function (event) {
    if (!this.isOpen() || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;

    const activePanel = this.panels[this.index];
    if (activePanel && activePanel.contains(document.activeElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.go(event.shiftKey ? -1 : 1);

    const nextPanel = this.panels[this.index];
    const focusable = getPanelFocusableElements(nextPanel);
    const target = event.shiftKey ? focusable[focusable.length - 1] : focusable[0];
    const fallback = this.viewer.querySelector('.stories-viewer__close');
    (target || fallback)?.focus({ preventScroll: true });
  };

  StoriesSection.prototype.onTouchStart = function (event) {
    this.touchStartX = event.changedTouches && event.changedTouches[0] ? event.changedTouches[0].clientX : 0;
  };

  StoriesSection.prototype.onTouchEnd = function (event) {
    const endX = event.changedTouches && event.changedTouches[0] ? event.changedTouches[0].clientX : this.touchStartX;
    const delta = endX - this.touchStartX;
    if (Math.abs(delta) < 48) return;
    this.go(delta > 0 ? -1 : 1);
  };

  StoriesSection.prototype.onBlockSelect = function (event) {
    const trigger = event.target && event.target.matches('[data-stories-trigger]')
      ? event.target
      : event.target && event.target.querySelector
        ? event.target.querySelector('[data-stories-trigger]')
        : null;
    if (!trigger) return;
    this.open(Number(trigger.dataset.storiesIndex || 0));
  };

  StoriesSection.prototype.isOpen = function () {
    return this.viewer && this.viewer.classList.contains(OPEN_CLASS);
  };

  StoriesSection.prototype.onRailScroll = function () {
    if (this.railFrame !== null) return;
    this.railFrame = window.requestAnimationFrame(() => {
      this.railFrame = null;
      this.updateRailControls();
    });
  };

  StoriesSection.prototype.updateRailControls = function () {
    if (!this.rail || !this.railControls || !this.railPrevious || !this.railNext) return;
    const cells = Array.from(this.rail.querySelectorAll('.stories__rail-cell'));
    const hasOverflow = this.rail.scrollWidth > this.rail.clientWidth + 1;
    this.railControls.hidden = !hasOverflow;
    if (!hasOverflow || !cells.length) {
      this.railPrevious.disabled = true;
      this.railNext.disabled = true;
      return;
    }

    const railRect = this.rail.getBoundingClientRect();
    const firstRect = cells[0].getBoundingClientRect();
    const lastRect = cells[cells.length - 1].getBoundingClientRect();
    const firstVisible = firstRect.left >= railRect.left - 1 && firstRect.right <= railRect.right + 1;
    const lastVisible = lastRect.left >= railRect.left - 1 && lastRect.right <= railRect.right + 1;
    this.railPrevious.disabled = firstVisible;
    this.railNext.disabled = lastVisible;
  };

  StoriesSection.prototype.scrollRail = function (direction) {
    if (!this.rail) return;
    const firstCell = this.rail.querySelector('.stories__rail-cell');
    if (!firstCell) return;
    const railStyles = window.getComputedStyle(this.rail);
    const gap = parseFloat(railStyles.columnGap || railStyles.gap) || 0;
    const step = firstCell.getBoundingClientRect().width + gap;
    const rtlFactor = railStyles.direction === 'rtl' ? -1 : 1;
    this.rail.scrollBy({
      left: step * direction * rtlFactor,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
    });
  };

  StoriesSection.prototype.playPreviewVideo = function (video) {
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play()
      .then(() => setPreviewToggleState(video, true))
      .catch(() => setPreviewToggleState(video, false));
  };

  StoriesSection.prototype.pausePreviewVideo = function (video) {
    if (!video) return;
    video.pause();
    setPreviewToggleState(video, false);
  };

  StoriesSection.prototype.syncPreviewVideo = function (video) {
    const shouldPlay = video.dataset.storiesPreviewVisible === 'true'
      && video.dataset.storiesPreviewPaused !== 'true'
      && !this.isOpen()
      && !document.hidden
      && !this.reducedMotion.matches;
    if (shouldPlay) {
      this.playPreviewVideo(video);
    } else {
      this.pausePreviewVideo(video);
    }
  };

  StoriesSection.prototype.syncPreviewVideos = function () {
    this.previewVideos.forEach((video) => this.syncPreviewVideo(video));
  };

  StoriesSection.prototype.pausePreviewVideos = function () {
    this.previewVideos.forEach((video) => this.pausePreviewVideo(video));
  };

  StoriesSection.prototype.togglePreviewVideo = function (button) {
    const card = button.closest('.stories-card');
    const video = card && card.querySelector('.stories-card__video');
    if (!video) return;
    if (video.paused) {
      video.dataset.storiesPreviewPaused = 'false';
      this.playPreviewVideo(video);
    } else {
      video.dataset.storiesPreviewPaused = 'true';
      this.pausePreviewVideo(video);
    }
  };

  StoriesSection.prototype.onVisibilityChange = function () {
    if (document.hidden) {
      this.pausePreviewVideos();
    } else {
      this.syncPreviewVideos();
    }
  };

  StoriesSection.prototype.open = function (index) {
    if (!this.viewer || !this.panels.length) return;
    this.index = clampIndex(index, this.panels.length);
    this.hydrateViewerMedia();
    this.ensureViewerReady();
    this.lastFocus = document.activeElement;
    this.pausePreviewVideos();
    this.setIndex(this.index, false);
    this.viewer.classList.add(OPEN_CLASS);
    this.viewer.setAttribute('aria-hidden', 'false');
    this.viewer.removeAttribute('inert');
    setPageLocked(true);
    document.addEventListener('keydown', this.boundKeydown);
    if (window.Axiom && typeof window.Axiom.trapFocus === 'function') {
      window.Axiom.trapFocus(this.viewer, { focusTarget: this.viewer.querySelector('.stories-viewer__close') });
    } else {
      const close = this.viewer.querySelector('.stories-viewer__close');
      if (close) close.focus({ preventScroll: true });
    }
    playVideo(this.panels[this.index], this.audioEnabled);
    this.updateAudioButton();
  };

  StoriesSection.prototype.close = function () {
    if (!this.viewer) return;
    this.panels.forEach(pauseVideo);
    this.viewer.classList.remove(OPEN_CLASS);
    this.viewer.setAttribute('aria-hidden', 'true');
    this.viewer.setAttribute('inert', '');
    setPageLocked(false);
    document.removeEventListener('keydown', this.boundKeydown);
    if (window.Axiom && typeof window.Axiom.releaseFocus === 'function') {
      window.Axiom.releaseFocus(this.viewer);
    }
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus({ preventScroll: true });
    }
    this.lastFocus = null;
    this.syncPreviewVideos();
  };

  StoriesSection.prototype.go = function (delta) {
    this.setIndex(this.index + delta, true);
  };

  StoriesSection.prototype.activeVideo = function () {
    const activePanel = this.panels[this.index];
    return activePanel ? activePanel.querySelector('video') : null;
  };

  StoriesSection.prototype.updateAudioButton = function () {
    if (!this.audioButtons.length) return;
    const activePanel = this.panels[this.index];

    this.audioButtons.forEach((button) => {
      const panel = button.closest('[data-stories-panel]');
      const isActiveVideo = panel === activePanel && Boolean(this.activeVideo());
      button.hidden = !isActiveVideo;
      button.setAttribute('aria-pressed', this.audioEnabled ? 'true' : 'false');
      button.setAttribute('aria-label', this.audioEnabled ? 'Turn sound off' : 'Turn sound on');
    });
  };

  StoriesSection.prototype.toggleAudio = function () {
    this.audioEnabled = !this.audioEnabled;
    const video = this.activeVideo();
    if (video) {
      video.muted = !this.audioEnabled;
      if (this.isOpen()) video.play().catch(() => {});
    }
    this.updateAudioButton();
  };

  StoriesSection.prototype.setIndex = function (index, shouldPlay) {
    const length = this.panels.length;
    this.index = clampIndex(index, length);

    this.panels.forEach((panel, panelIndex) => {
      const offset = panelIndex - this.index;
      const isActive = panelIndex === this.index;
      const isPrev = offset === -1 || offset === length - 1;
      const isNext = offset === 1 || offset === 1 - length;
      panel.classList.toggle('is-active', panelIndex === this.index);
      panel.classList.toggle('is-prev', isPrev);
      panel.classList.toggle('is-next', isNext);
      panel.classList.toggle('is-far-prev', offset < -1 && offset > -length + 1);
      panel.classList.toggle('is-far-next', offset > 1 && offset < length - 1);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive || isPrev || isNext) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
      setPanelFocusEnabled(panel, isActive);
      if (!isActive) pauseVideo(panel);
    });

    this.progress.forEach((item, itemIndex) => {
      item.classList.toggle('is-active', itemIndex === this.index);
      item.classList.toggle('is-seen', itemIndex < this.index);
    });

    this.updateAudioButton();
    if (shouldPlay && this.isOpen()) playVideo(this.panels[this.index], this.audioEnabled);
  };

  StoriesSection.prototype.destroy = function () {
    this.close();
    this.root.removeEventListener('click', this.boundClick);
    if (this.viewer) {
      this.viewer.removeEventListener('click', this.boundViewerClick);
      this.viewer.removeEventListener('keydown', this.boundViewerKeydown, true);
      this.viewer.removeEventListener('touchstart', this.boundTouchStart);
      this.viewer.removeEventListener('touchend', this.boundTouchEnd);
      if (this.viewer.dataset.storiesPortal === 'body' && this.viewer.parentElement === document.body) {
        this.viewer.remove();
      }
    }
    this.root.removeEventListener('shopify:block:select', this.boundBlockSelect);
    this.root.removeEventListener('shopify:section:unload', this.boundSectionUnload);
    if (this.rail) this.rail.removeEventListener('scroll', this.boundRailScroll);
    window.removeEventListener('resize', this.boundRailResize);
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    if (this.railResizeObserver) this.railResizeObserver.disconnect();
    if (this.previewObserver) this.previewObserver.disconnect();
    if (this.viewerMediaObserver) this.viewerMediaObserver.disconnect();
    if (this.railFrame !== null) window.cancelAnimationFrame(this.railFrame);
    this.pausePreviewVideos();
    this.panels.forEach(pauseVideo);
    delete this.root._storiesController;
  };

  // ── SPOTLIGHT ─────────────────────────────────
  // One large reel in the middle and smaller reels around it in a loop. The
  // large reel plays its video and opens the Stories viewer; the others, the
  // arrows, swipes and arrow keys bring a reel to the middle. The product or
  // collection of the reel in the middle shows underneath.
  function StoriesSpotlight(root) {
    this.root = root;
    this.el = root.querySelector('[data-stories-spotlight]');
    this.stage = this.el.querySelector('[data-spotlight-stage]');
    this.reels = Array.from(this.el.querySelectorAll('[data-spotlight-reel]'));
    this.products = Array.from(this.el.querySelectorAll('[data-spotlight-product]'));
    this.previews = this.el.dataset.spotlightPreviews === 'true';
    this.index = 0;
    this.offsets = [];
    this.soundOn = false;
    this.paused = false;
    this.inView = false;
    this.touchX = null;
    this.frame = null;
    this.boundClick = this.onClick.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundTouchStart = this.onTouchStart.bind(this);
    this.boundTouchEnd = this.onTouchEnd.bind(this);
    this.boundResize = this.schedule.bind(this);
    this.boundBlockSelect = this.onBlockSelect.bind(this);
    this.boundUnload = this.destroy.bind(this);
    this.boundVisibility = this.syncVideos.bind(this);
    this.init();
  }

  StoriesSpotlight.prototype.init = function () {
    this.el.addEventListener('click', this.boundClick);
    this.el.addEventListener('keydown', this.boundKeydown);
    this.stage.addEventListener('touchstart', this.boundTouchStart, { passive: true });
    this.stage.addEventListener('touchend', this.boundTouchEnd);
    window.addEventListener('resize', this.boundResize, { passive: true });
    document.addEventListener('visibilitychange', this.boundVisibility);
    this.root.addEventListener('shopify:block:select', this.boundBlockSelect);
    this.root.addEventListener('shopify:section:unload', this.boundUnload);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries.some((entry) => entry.isIntersecting);
        this.syncVideos();
      }, { threshold: 0.25 });
      this.observer.observe(this.stage);
    } else {
      this.inView = true;
    }
    this.layout();
    this.el.classList.add('is-ready');
  };

  // Where reel i sits relative to the middle: 0 is the middle, negative left.
  // Three reels or more loop, so the middle always has reels on both sides.
  StoriesSpotlight.prototype.offsetOf = function (i) {
    const count = this.reels.length;
    if (count < 3) return i - this.index;
    let offset = ((i - this.index) % count + count) % count;
    if (offset > count / 2) offset -= count;
    return offset;
  };

  StoriesSpotlight.prototype.layout = function () {
    this.frame = null;
    if (!this.reels.length) return;
    const style = window.getComputedStyle(this.el);
    const width = this.reels[0].offsetWidth;
    const scale = parseFloat(style.getPropertyValue('--spotlight-side-scale')) || 0.86;
    const gap = parseFloat(style.getPropertyValue('--spotlight-gap')) || 0;
    const gapActive = parseFloat(style.getPropertyValue('--spotlight-gap-active')) || 0;
    const half = this.stage.clientWidth / 2;
    this.reels.forEach((reel, i) => {
      const offset = this.offsetOf(i);
      const distance = Math.abs(offset);
      const x = offset === 0
        ? 0
        : Math.sign(offset) * (width / 2 + gapActive + (width * scale) / 2 + (distance - 1) * (width * scale + gap));
      // A reel that wraps around to the other side jumps there instead of
      // crossing the stage.
      const previous = this.offsets[i];
      const jump = previous !== undefined && Math.abs(offset - previous) > 1;
      if (jump) reel.style.transition = 'none';
      reel.style.transform = 'translate(-50%, -50%) translateX(' + x.toFixed(2) + 'px) scale(' + (offset === 0 ? 1 : scale) + ')';
      if (jump) {
        void reel.offsetWidth;
        reel.style.transition = '';
      }
      this.offsets[i] = offset;
      const active = offset === 0;
      reel.classList.toggle('is-active', active);
      reel.classList.toggle('is-far', Math.abs(x) - (width * scale) / 2 > half);
      reel.toggleAttribute('aria-current', active);
      const trigger = reel.querySelector('[data-spotlight-trigger]');
      if (trigger) trigger.tabIndex = active || distance === 1 ? 0 : -1;
      reel.querySelectorAll('.stories-spotlight__video-button').forEach((button) => {
        button.tabIndex = active ? 0 : -1;
      });
    });
    this.products.forEach((product) => {
      const active = Number(product.dataset.index) === this.index;
      product.hidden = !active;
      product.classList.toggle('is-active', active);
    });
    this.syncVideos();
  };

  StoriesSpotlight.prototype.schedule = function () {
    if (this.frame === null) this.frame = window.requestAnimationFrame(this.layout.bind(this));
  };

  StoriesSpotlight.prototype.go = function (index) {
    const count = this.reels.length;
    if (!count) return;
    this.index = count < 3 ? clampIndex(index, count) : ((index % count) + count) % count;
    this.paused = false;
    this.layout();
  };

  StoriesSpotlight.prototype.syncVideos = function () {
    const visible = this.inView && document.visibilityState !== 'hidden';
    this.reels.forEach((reel, i) => {
      const video = reel.querySelector('video');
      if (!video) return;
      const active = i === this.index;
      video.muted = !(active && this.soundOn);
      const play = visible && (active ? !this.paused : this.previews);
      if (play) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
      const playback = reel.querySelector('[data-spotlight-playback]');
      if (playback) {
        const playing = active && !this.paused;
        playback.setAttribute('aria-pressed', String(playing));
        playback.setAttribute('aria-label', playing ? playback.dataset.labelPause : playback.dataset.labelPlay);
      }
      const sound = reel.querySelector('[data-spotlight-sound]');
      if (sound) {
        const on = active && this.soundOn;
        sound.setAttribute('aria-pressed', String(on));
        sound.setAttribute('aria-label', on ? sound.dataset.labelOff : sound.dataset.labelOn);
      }
    });
  };

  StoriesSpotlight.prototype.onClick = function (event) {
    if (event.target.closest('[data-spotlight-previous]')) {
      this.go(this.index - 1);
      return;
    }
    if (event.target.closest('[data-spotlight-next]')) {
      this.go(this.index + 1);
      return;
    }
    if (event.target.closest('[data-spotlight-sound]')) {
      this.soundOn = !this.soundOn;
      this.syncVideos();
      return;
    }
    if (event.target.closest('[data-spotlight-playback]')) {
      this.paused = !this.paused;
      this.syncVideos();
      return;
    }
    const trigger = event.target.closest('[data-spotlight-trigger]');
    if (!trigger) return;
    const reel = trigger.closest('[data-spotlight-reel]');
    const index = this.reels.indexOf(reel);
    if (index < 0) return;
    if (index !== this.index) {
      this.go(index);
      return;
    }
    const stories = this.root._storiesController;
    if (stories && typeof stories.open === 'function') {
      this.paused = true;
      this.syncVideos();
      stories.open(index);
    }
  };

  StoriesSpotlight.prototype.onKeydown = function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.go(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
    const trigger = this.reels[this.index] && this.reels[this.index].querySelector('[data-spotlight-trigger]');
    if (trigger) trigger.focus({ preventScroll: true });
  };

  StoriesSpotlight.prototype.onTouchStart = function (event) {
    this.touchX = event.changedTouches[0].clientX;
  };

  StoriesSpotlight.prototype.onTouchEnd = function (event) {
    if (this.touchX === null) return;
    const delta = event.changedTouches[0].clientX - this.touchX;
    this.touchX = null;
    if (Math.abs(delta) < 40) return;
    this.go(this.index + (delta < 0 ? 1 : -1));
  };

  StoriesSpotlight.prototype.onBlockSelect = function (event) {
    const reel = event.target && event.target.closest && event.target.closest('[data-spotlight-reel]');
    const index = this.reels.indexOf(reel);
    if (index >= 0) this.go(index);
  };

  StoriesSpotlight.prototype.destroy = function () {
    this.el.removeEventListener('click', this.boundClick);
    this.el.removeEventListener('keydown', this.boundKeydown);
    this.stage.removeEventListener('touchstart', this.boundTouchStart);
    this.stage.removeEventListener('touchend', this.boundTouchEnd);
    window.removeEventListener('resize', this.boundResize);
    document.removeEventListener('visibilitychange', this.boundVisibility);
    this.root.removeEventListener('shopify:block:select', this.boundBlockSelect);
    this.root.removeEventListener('shopify:section:unload', this.boundUnload);
    if (this.observer) this.observer.disconnect();
    if (this.frame !== null) window.cancelAnimationFrame(this.frame);
    this.reels.forEach((reel) => {
      const video = reel.querySelector('video');
      if (video) video.pause();
    });
    delete this.root._storiesSpotlight;
  };

  function initStories(root) {
    const sections = root && root.matches && root.matches(SELECTOR)
      ? [root]
      : Array.from((root || document).querySelectorAll(SELECTOR));

    sections.forEach((section) => {
      if (!section._storiesSpotlight && section.querySelector('[data-stories-spotlight]')) {
        section._storiesSpotlight = new StoriesSpotlight(section);
      }
      if (section._storiesController) return;
      if (!section.querySelector('[data-stories-viewer] [data-stories-panel]')) return;
      section._storiesController = new StoriesSection(section);
    });
  }

  window.AxiomStoriesInit = initStories;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStories(document));
  } else {
    initStories(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initStories(event.target);
  });
})();
